import type { NewsCategory, NewsLang } from '../services/generated/Api';
import type { Language } from '../i18n/types';
import { LANGUAGE_CODES, stripLangPrefix, withLangPrefix } from '../i18n/locale';

// WHY: модуль тянут клиентские Providers — значения перечислений из сгенерированного клиента на 20 тысяч строк сюда не
// импортируем, совпадение списков с generated API держат типы и тесты
const NEWS_LANG_CODES: readonly string[] = ['ru', 'en', 'uz', 'fa', 'pt', 'tr', 'uk', 'de', 'es', 'pl'] satisfies readonly `${NewsLang}`[];
const NEWS_CATEGORY_CODES: readonly string[] = [
  'tournaments_matches',
  'teams_transfers',
  'players_community',
  'cs2_updates',
  'rankings_statistics',
  'industry_rules',
] satisfies readonly `${NewsCategory}`[];

const isNewsLang = (value: string | null | undefined): value is NewsLang => NEWS_LANG_CODES.some((code) => code === value);
const isNewsCategory = (value: string | null | undefined): value is NewsCategory => NEWS_CATEGORY_CODES.some((code) => code === value);

export const NEWS_LANGS: NewsLang[] = NEWS_LANG_CODES.filter(isNewsLang);

export const toNewsLang = (value: string | null | undefined): NewsLang | null => (isNewsLang(value) ? value : null);

export const NEWS_X_DEFAULT_LANG: NewsLang = toNewsLang('en') ?? NEWS_LANGS[0];

export const NEWS_PATH = '/news';

export const NEWS_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const newsArticlePath = (slug: string): string => `${NEWS_PATH}/${slug}`;

// WHY: у каждого перевода свой адрес, а языкам без перевода этой новости (uk, de, es, pl необязательны) некуда вести, кроме их главной
export const newsLanguageAlternates = (
  translations: { lang: NewsLang; slug: string }[],
): Partial<Record<Language, string>> =>
  Object.fromEntries(
    LANGUAGE_CODES.map((code) => {
      const translation = translations.find((item) => item.lang === code);
      return [code, translation ? withLangPrefix(code, newsArticlePath(translation.slug)) : withLangPrefix(code, '/')];
    }),
  );

// WHY: смена языка меняет только префикс адреса, а у переводов могут быть свои slug — бэкенд отдаёт версию языка
// по slug другой локали, и страница уводит на её собственный адрес
export const newsArticleRedirect = (requestedSlug: string, article: { lang: NewsLang; slug: string }): string | null =>
  article.slug === requestedSlug ? null : withLangPrefix(article.lang, newsArticlePath(article.slug));

export const newsListHref = (lang: NewsLang, page = 1, category: NewsCategory | null = null): string => {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (page > 1) params.set('page', String(page));
  const query = params.toString();
  return withLangPrefix(lang, query ? `${NEWS_PATH}?${query}` : NEWS_PATH);
};

export const isNewsPath = (pathname: string): boolean => /^\/news(\/|$)/.test(stripLangPrefix(pathname));

export const parseNewsCategory = (raw: string | string[] | undefined): NewsCategory | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isNewsCategory(value) ? value : null;
};

export const parseNewsPage = (raw: string | string[] | undefined): number | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (value === undefined) return 1;
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 ? page : null;
};
